import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { cn } from '../lib/cn';
import { Loading } from './Loading';

/** One entry, in the shape both services' audit endpoints return. */
export interface HistoryEntry {
  id: string;
  changeSetId: string;
  occurredUtc: string;
  /** Email, `svc:{client}` for an integration, or "system". */
  actorKey: string;
  entityType: string;
  entityId: string;
  /** `Overridden`: a proposed value changed by hand (field → from / to, like Updated). */
  action: 'Created' | 'Updated' | 'Deleted' | 'Restored' | 'Overridden';
  /** `{"Status":{"from":"Draft","to":"Open"}}`, or a `{to}` / `{from}` snapshot. */
  changesJson: string | null;
}

export interface HistoryTimelineProps {
  entries: HistoryEntry[];
  isLoading?: boolean;
  error?: string | null;
  /**
   * Renames a field for display — `Reference1` means nothing to an operator who
   * calls it "Customer PO". Returns null to hide a field entirely.
   */
  fieldLabel?: (entityType: string, field: string) => string | null;
  /** Renames a record type: "OrderTaskCargoItem" reads better as "Cargo". */
  entityLabel?: (entityType: string) => string;
  /** Shown when there is nothing yet, which is every record older than the trail. */
  emptyMessage?: string;

  /** More pages exist — the list ends in a sentinel that asks for them. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * The element that actually scrolls. The list usually sits inside a dialog
   * rather than the page, and an observer left on the viewport would never fire
   * for it — the sentinel is off-screen from the viewport's point of view the
   * whole time.
   */
  scrollRootRef?: RefObject<HTMLElement | null>;
}

const ACTION_TONE: Record<HistoryEntry['action'], string> = {
  Created: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  Updated: 'bg-sky-50 text-sky-700 ring-sky-200',
  Deleted: 'bg-red-50 text-red-700 ring-red-200',
  Restored: 'bg-amber-50 text-amber-700 ring-amber-200',
  Overridden: 'bg-violet-50 text-violet-700 ring-violet-200',
};

export interface ParsedChange {
  field: string;
  from: unknown;
  to: unknown;
  /** A create or delete carries the row, not a pair — rendered as one column. */
  snapshot: boolean;
}

/**
 * Reads the stored JSON without trusting it.
 *
 * <p>
 * The column is written by a server that may be a version ahead of this bundle,
 * and it is the one part of the payload with no schema. A malformed or unknown
 * shape renders as nothing rather than throwing away the whole entry — the
 * timestamp and the actor are the part nobody can afford to lose.
 * </p>
 */
export function parseAuditChanges(changesJson: string | null): ParsedChange[] {
  if (!changesJson) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(changesJson);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];

  const root = parsed as Record<string, unknown>;

  // Snapshot shape: one key, `to` (created) or `from` (deleted), holding the row.
  for (const key of ['to', 'from'] as const) {
    const snapshot = root[key];
    if (Object.keys(root).length === 1 && snapshot && typeof snapshot === 'object') {
      return Object.entries(snapshot as Record<string, unknown>).map(([field, value]) => ({
        field,
        from: key === 'from' ? value : undefined,
        to: key === 'to' ? value : undefined,
        snapshot: true,
      }));
    }
  }

  return Object.entries(root).flatMap(([field, value]) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    const pair = value as Record<string, unknown>;
    if (!('from' in pair) && !('to' in pair)) return [];
    return [{ field, from: pair.from, to: pair.to, snapshot: false }];
  });
}

export function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** "sebastian@stratum.ie" → "sebastian", "svc:NightlySync" → "NightlySync". */
export function formatAuditActor(actorKey: string): string {
  if (actorKey.startsWith('svc:')) return actorKey.slice(4);
  const at = actorKey.indexOf('@');
  return at > 0 ? actorKey.slice(0, at) : actorKey;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * How many fields a single entry shows before folding the rest away.
 *
 * <p>
 * Six covers a normal edit outright, so the fold never appears where it is not
 * needed. It exists for the creates: a new customer legitimately sets twenty
 * columns at once, and an entry that long stops being a timeline and becomes a
 * table — while the next entry, the one the reader probably came for, is pushed
 * off the screen.
 * </p>
 */
const COLLAPSE_AFTER = 6;

function ChangeList({
  changes,
  label,
}: {
  changes: ParsedChange[];
  label: (field: string) => string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? changes : changes.slice(0, COLLAPSE_AFTER);
  const hidden = changes.length - visible.length;

  return (
    <>
      <ul className="mt-0.5 space-y-0.5 pl-1">
        {visible.map((change) => {
          const name = label(change.field);
          if (name === null) return null;
          return (
            <li key={change.field} className="flex flex-wrap items-baseline gap-1">
              <span className="text-slate-500">{name}</span>
              {change.snapshot ? (
                <span className="font-medium text-slate-700">
                  {formatAuditValue(change.to ?? change.from)}
                </span>
              ) : (
                <>
                  <span className="text-slate-400 line-through">
                    {formatAuditValue(change.from)}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium text-slate-800">{formatAuditValue(change.to)}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
      {(hidden > 0 || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-0.5 pl-1 text-[11px] font-medium text-purple-600 hover:underline"
        >
          {expanded ? 'Show less' : `Show ${hidden} more`}
        </button>
      )}
    </>
  );
}

/**
 * A record's history, newest first.
 *
 * <p>
 * Entries are grouped by the save that produced them, because one click on Save
 * is one thing that happened — showing an order, three of its tasks and a charge
 * line as five separate events makes the reader reconstruct what the operator
 * actually did. The group carries the who and the when; each row inside says
 * what moved.
 * </p>
 *
 * <p>
 * Presentational on purpose: it takes entries and renders them, so both services'
 * trails and every app can use the same component without it knowing where the
 * rows came from.
 * </p>
 */
export function HistoryTimeline({
  entries,
  isLoading = false,
  error = null,
  fieldLabel,
  entityLabel,
  emptyMessage = 'Nothing recorded yet.',
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  scrollRootRef,
}: HistoryTimelineProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || !onLoadMore || isLoadingMore) return;
    if (typeof IntersectionObserver === 'undefined') return;

    // `rootMargin` asks one page ahead of the bottom, so the next entries are
    // usually there by the time the reader gets to them.
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { root: scrollRootRef?.current ?? null, rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore, scrollRootRef, entries.length]);
  const groups = useMemo(() => {
    const byChangeSet = new Map<string, HistoryEntry[]>();
    for (const entry of entries) {
      const existing = byChangeSet.get(entry.changeSetId);
      if (existing) existing.push(entry);
      else byChangeSet.set(entry.changeSetId, [entry]);
    }
    return [...byChangeSet.values()];
  }, [entries]);

  if (error) {
    return <p className="px-3 py-4 text-xs text-red-600">{error}</p>;
  }
  if (isLoading) {
    // The shared spinner, not a line of italic text: a dialog that opens to a
    // sentence looks like it has already finished and found nothing.
    return <Loading label="Loading history…" className="px-3 py-6" />;
  }
  if (entries.length === 0) {
    return <p className="px-3 py-4 text-xs italic text-slate-400">{emptyMessage}</p>;
  }

  return (
    <>
      <ol className="divide-y divide-slate-100">
        {groups.map((group) => {
          const first = group[0]!;
          return (
            <li key={first.changeSetId} className="px-3 py-2.5">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-xs font-semibold text-slate-800">
                  {formatAuditActor(first.actorKey)}
                </span>
                <span className="text-[11px] text-slate-500">{formatWhen(first.occurredUtc)}</span>
                {first.actorKey.startsWith('svc:') && (
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                    integration
                  </span>
                )}
              </div>

              <div className="mt-1 space-y-1">
                {group.map((entry) => {
                  const changes = parseAuditChanges(entry.changesJson);
                  const label = entityLabel?.(entry.entityType) ?? entry.entityType;
                  return (
                    <div key={entry.id} className="text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset',
                            ACTION_TONE[entry.action],
                          )}
                        >
                          {entry.action}
                        </span>
                        <span className="font-medium text-slate-700">{label}</span>
                      </div>

                      {changes.length > 0 && (
                        <ChangeList
                          changes={changes}
                          label={(field) => fieldLabel?.(entry.entityType, field) ?? field}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ol>

      {/* The sentinel loads the next page on approach. The button beneath is
          not a fallback nobody sees: an observer needs layout, and a keyboard
          reader tabbing through never scrolls the container at all. */}
      {hasMore && (
        <div ref={sentinelRef} className="px-3 py-2">
          {isLoadingMore ? (
            <Loading label="Loading more…" className="text-[11px]" />
          ) : (
            <button
              type="button"
              onClick={() => onLoadMore?.()}
              className="text-[11px] font-medium text-purple-600 hover:underline"
            >
              Load older entries
            </button>
          )}
        </div>
      )}
    </>
  );
}
